import { z } from 'zod'
import { FIELD_CANNOT_BE_EMPTY } from '../../../../consts/validationErrors'
import { SOCIAL_ORDER_FOCUS_VALUES } from '../../../../consts/socialOrderFocus'

const IMAGE_REQUIRED = 'Загрузите изображение социального заказа'
const URL_INVALID = 'Укажите корректную ссылку (с https://)'

const problemSchema = z
  .string({ required_error: FIELD_CANNOT_BE_EMPTY })
  .trim()
  .min(1, { message: FIELD_CANNOT_BE_EMPTY })
  .min(2, { message: 'Минимально 2 символа' })
  .max(500, { message: 'Максимально 500 символов' })

export const socialOrderFormSchema = z.object({
  title: z
    .string({ required_error: FIELD_CANNOT_BE_EMPTY })
    .trim()
    .min(1, { message: FIELD_CANNOT_BE_EMPTY })
    .min(2, { message: 'Минимально 2 символа' })
    .max(240, { message: 'Максимально 240 символа' }),
  description: z
    .string({ required_error: FIELD_CANNOT_BE_EMPTY })
    .trim()
    .min(1, { message: FIELD_CANNOT_BE_EMPTY })
    .min(2, { message: 'Минимально 2 символа' })
    .max(2000, { message: 'Максимально 2000 символов' }),
  problem: problemSchema,
  focus: z
    .array(z.string())
    .min(1, { message: 'Выберите хотя бы одну направленность' })
    .refine(
      (arr) => arr.every((v) => SOCIAL_ORDER_FOCUS_VALUES.includes(v)),
      { message: 'Допустимы только значения из списка направленностей' }
    ),
})

export const createSocialOrderFormSchema = socialOrderFormSchema.extend({
  image: z.union(
    [z.instanceof(File), z.string().trim().min(1, { message: IMAGE_REQUIRED })],
    { required_error: IMAGE_REQUIRED },
  ),
})

export const editSocialOrderFormSchema = socialOrderFormSchema
  .extend({
    is_completed: z.boolean(),
    completion_description: z.string(),
    completion_resource_url: z.string(),
  })
  .superRefine((data, ctx) => {
    if (!data.is_completed) return

    const completionDescription = data.completion_description.trim()
    if (completionDescription.length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: completionDescription.length === 0 ? FIELD_CANNOT_BE_EMPTY : 'Минимально 2 символа',
        path: ['completion_description'],
      })
    } else if (completionDescription.length > 500) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Максимально 500 символов',
        path: ['completion_description'],
      })
    }

    const url = data.completion_resource_url.trim()
    if (url.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: FIELD_CANNOT_BE_EMPTY,
        path: ['completion_resource_url'],
      })
      return
    }
    if (url.length > 2048) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Максимально 2048 символов',
        path: ['completion_resource_url'],
      })
      return
    }
    try {
      new URL(url)
    } catch {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: URL_INVALID,
        path: ['completion_resource_url'],
      })
    }
  })

export type SocialOrderFormValues = z.infer<typeof socialOrderFormSchema>
export type CreateSocialOrderFormValues = z.infer<typeof createSocialOrderFormSchema>
export type EditSocialOrderFormValues = z.infer<typeof editSocialOrderFormSchema>
